import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { GuidesApiService } from '@uc/web/shared/data-access';
import {
	fetchQuizOptions,
	fetchQuizQuestions,
	fetchQuizResult,
	fetchSubjectGuides,
	updateDegreeQuizError,
	updateQuizOptions,
	updateQuizQuestions,
	updateQuizResult,
	updateQuizSubmitted,
	updateQuizVersion,
	updateSubjectGuides,
} from './degree-quiz.actions';
import {
	catchError,
	distinctUntilChanged,
	filter,
	forkJoin,
	map,
	of,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';
import { LocalStorageService } from '@uc/utilities';
import { SingleGuide } from '@uc/web/shared/data-models';
import { GUIDE_SLUGS } from '../../static/degree-quiz-home.properties';
import { DegreeQuizGuideMeta } from '../../models/degree-quiz.models';
import {
	getDegreeQuizResultId,
	getDegreeQuizStateQuizOptions,
	getDegreeQuizStateQuizQuestions,
	getDegreeQuizStateSubjectGuides,
	getDegreeQuizVersion,
} from './degree-quiz.selectors';
import { DegreeQuizApiService } from '../degree-quiz-api.service';
import {
	LS_DEGREE_QUIZ_SUBMITTED,
	LS_DEGREE_QUIZ_VERSION,
} from '../../static/degree-quiz-local-storage.properties';

@Injectable({ providedIn: 'root' })
export class StateDegreeQuizEffects {
	constructor(
		private _actions$: Actions,
		private _store: Store,
		private _degreeQuizApiSrv: DegreeQuizApiService,
		private _guidesApiSrv: GuidesApiService,
		private _localStorageSrv: LocalStorageService,
	) {}

	fetchQuizOptions = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchQuizOptions),
			withLatestFrom(this._store.select(getDegreeQuizStateQuizOptions)),
			distinctUntilChanged(),
			filter(([, options]) => options.length === 0),
			switchMap(() =>
				this._degreeQuizApiSrv.getQuizOptions().pipe(
					map((options) => {
						return updateQuizOptions({ options });
					}),
					catchError((error) => {
						console.error(error);
						return of(updateDegreeQuizError(error));
					}),
				),
			),
		);
	});

	fetchQuizQuestions = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchQuizQuestions),
			withLatestFrom(
				this._store.select(getDegreeQuizStateQuizQuestions),
				this._store.select(getDegreeQuizVersion),
			),
			distinctUntilChanged(),
			filter(
				([, questions, version]) =>
					questions.length === 0 || version === 'continuation',
			),
			switchMap(([{ quizSlug }]) =>
				this._degreeQuizApiSrv.getQuizById(quizSlug).pipe(
					map(({ questions }) => {
						const sortedQuestions = questions.sort(
							(a, b) => a.order - b.order,
						);
						return updateQuizQuestions({ questions: sortedQuestions });
					}),
					catchError((error) => {
						console.error(error);
						return of(updateDegreeQuizError(error));
					}),
				),
			),
		);
	});

	fetchQuizResult = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchQuizResult),
			withLatestFrom(this._store.select(getDegreeQuizResultId)),
			switchMap(([, resultId]) =>
				this._degreeQuizApiSrv.getQuizResult(resultId).pipe(
					map((result) => {
						return updateQuizResult({ result });
					}),
					catchError((error) => {
						console.error(error);
						return of(updateDegreeQuizError({ error }));
					}),
				),
			),
		);
	});

	fetchGuides = createEffect(() => {
		return this._actions$.pipe(
			ofType(fetchSubjectGuides),
			withLatestFrom(this._store.select(getDegreeQuizStateSubjectGuides)),
			distinctUntilChanged(),
			filter(([, guides]) => guides.length === 0),
			switchMap(() =>
				forkJoin([
					this._guidesApiSrv.getIndividualGuide(GUIDE_SLUGS[0], 1),
					this._guidesApiSrv.getIndividualGuide(GUIDE_SLUGS[1], 1),
					this._guidesApiSrv.getIndividualGuide(GUIDE_SLUGS[2], 1),
					this._guidesApiSrv.getIndividualGuide(GUIDE_SLUGS[3], 1),
					this._guidesApiSrv.getIndividualGuide(GUIDE_SLUGS[4], 1),
					this._guidesApiSrv.getIndividualGuide(GUIDE_SLUGS[5], 1),
				]).pipe(
					map((guides) => {
						const mappedGuides = this._mapGuides(guides);
						return updateSubjectGuides({ guides: mappedGuides });
					}),
					catchError((error) => {
						console.error(error);
						return of(updateDegreeQuizError(error));
					}),
				),
			),
		);
	});

	// local storage side effects
	updateQuizSubmitted = createEffect(
		() => {
			return this._actions$.pipe(
				ofType(updateQuizSubmitted),
				tap((payload) => {
					this._localStorageSrv.setItem(
						LS_DEGREE_QUIZ_SUBMITTED,
						JSON.stringify(payload.quizSubmitted),
					);
				}),
			);
		},
		{ dispatch: false },
	);

	updateQuizVersion = createEffect(
		() => {
			return this._actions$.pipe(
				ofType(updateQuizVersion),
				tap((payload) => {
					this._localStorageSrv.setItem(
						LS_DEGREE_QUIZ_VERSION,
						payload.quizVersion,
					);
				}),
			);
		},
		{ dispatch: false },
	);

	private _mapGuides(guides: SingleGuide[]) {
		const result: DegreeQuizGuideMeta[] = [];

		guides.forEach((guide) => {
			result.push({
				gid: guide.guiid,
				slug: `/guides/subject/${guide.seourl}`,
				date: guide.date,
				imgUrl: guide.imgname,
				title: guide.title,
			});
		});

		return result;
	}
}
